class QuickAddHandler
{
    constructor()
    {
        this.cartItemIds = new Map(); // Store cartItemId for each productId
        this.init();
    }

    init()
    {
        // Listen for AddedToCart event with quickAdd flag
        window.eventBus.subscribe('AddedToCart', (execution, data) =>
        {
            if (execution === 'OnTrigger' && data && data.quickadd === true)
                this.handleQuickAdd(data);
        });

        // Bind quantity controls (using event delegation)
        this.bindQuantityControls();
    }

    handleQuickAdd(data)
    {
        const productId = data.productid;
        const quantity = parseFloat(data.quantity);
        const quantityUnit = data.quantityunit || 'ks';
        const cartItemId = data.cartItemId; // From API response

        // If this is just an update (quantity change), skip the replacement
        if (data.update === 'true')
            return;

        // Store the cartItemId for this product
        if (cartItemId)
            this.cartItemIds.set(productId, cartItemId);

        // The load-data element in the form will automatically replace
        // the form with the quantity selector from the server
        // We just need to build the query string
        const forms = document.querySelectorAll(`form[data-productid="${productId}"].quick-add-form`);

        if (!forms)
            return;

        for (let form of forms)
        {
            const loadData = form.querySelector('load-data');

            if (!loadData)
                return;

            // Update the query string for the load-data element
            loadData.dataset.query = `?productId=${productId}&quantity=${quantity}&quantityUnit=${quantityUnit}&cartItemId=${cartItemId || ''}`;
        }
    }

    bindQuantityControls()
    {
        document.addEventListener('click', (e) =>
        {
            // Handle increase
            if (e.target.closest('.quantity-increase'))
            {
                const btn = e.target.closest('.quantity-increase');

                if (btn.closest('.product-quantity-controls[data-direct-mode="true"]'))
                    return;

                // Navigate up to find the container with data-product-id
                // Could be either product-cart-actions or product-quantity-controls parent
                const container = btn.closest('[data-product-id]');

                if (!container)
                    return;

                const productId = container.dataset.productId;

                this.adjustQuantity(productId, 1);
            }

            // Handle decrease
            if (e.target.closest('.quantity-decrease'))
            {
                const btn = e.target.closest('.quantity-decrease');

                if (btn.closest('.product-quantity-controls[data-direct-mode="true"]'))
                    return;

                // Navigate up to find the container with data-product-id
                // Could be either product-cart-actions or product-quantity-controls parent
                const container = btn.closest('[data-product-id]');

                if (!container)
                    return;

                const productId = container.dataset.productId;

                this.adjustQuantity(productId, -1);
            }
        });
    }

    async adjustQuantity(productId, delta)
    {
        const container = document.querySelector(
            `[data-product-id="${productId}"] .product-quantity-controls`
        );

        if (!container)
            return;

        const input = container.querySelector('.quantity-input');
        const currentQuantity = parseFloat(input.value);
        const minQuantity = parseFloat(input.min);
        const step = parseFloat(input.step);

        let newQuantity = currentQuantity + (delta * step);

        // If quantity goes to 0 or below, restore the "Add to Cart" button
        if (newQuantity <= 0)
        {
            await this.removeFromCart(productId);

            return;
        }

        // Update UI immediately for responsiveness
        const previousQuantity = currentQuantity;

        input.value = newQuantity;

        // Find the hidden update form
        const updateForm = container.parentElement.querySelector('.quantity-update-form');

        if (!updateForm)
        {
            input.value = previousQuantity;

            return;
        }

        // Update form data with new quantity
        updateForm.dataset.quantity = newQuantity;

        // Get the cartItemId
        const cartItemId = this.cartItemIds.get(parseInt(productId)) || updateForm.dataset.itemId;

        if (!cartItemId)
        {
            console.error('Cart item ID not found for product:', productId);

            input.value = previousQuantity;

            return;
        }

        // Update item-id for PUT request
        updateForm.dataset.itemId = cartItemId;

        try
        {
            await window.submitApiForm(updateForm);
        }
        catch (error)
        {
            console.error('Failed to update quantity:', error);

            // Revert on error
            input.value = previousQuantity;
        }
    }

    async removeFromCart(productId)
    {
        const containers = document.querySelectorAll(`.product-cart-actions[data-product-id="${productId}"]`);

        if (!containers)
        {
            console.error('Quick-add container not found for product:', productId);

            return;
        }

        const cartItemId = this.cartItemIds.get(parseInt(productId));

        if (!cartItemId)
        {
            console.error('Cart item ID not found for product:', productId);

            return;
        }

        // Remove the item from cart by setting quantity to 0 or using Delete endpoint
        // We'll fetch the quick-add form to restore the "Add to Cart" button
        try
        {
            // Call the remove endpoint
            const response = await fetch(`${document.baseURI}api/cart/remove`,
            {
                method: 'DELETE',
                headers:
                {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    itemId: cartItemId
                })
            });

            if (!response.ok)
                throw new Error('Failed to remove item from cart');

            eventBus.broadcast("RemovedFromCart", "OnTrigger",
            {
                itemId: cartItemId,
                productId: productId,
                quantity: 1
            });

            // Fetch the quick-add form to restore the button
            const formResponse = await fetch(`${document.baseURI}cart/quick-add-form?productId=${productId}`);

            if (!formResponse.ok)
                throw new Error('Failed to load quick add form');

            const html = await formResponse.text();

            // Remove from our map
            this.cartItemIds.delete(parseInt(productId));

            for(let container of containers)
            {
                container.innerHTML = html;

                this.ensureUniqueQuickAddFormTarget(container);

                // Initialize any scripts in the new HTML
                if (window.abra && typeof window.abra.init === 'function')
                    window.abra.init(container);
            }
        }
        catch (error)
        {
            console.error('Failed to remove from cart:', error);
        }

        // Trigger cart update events
        window.eventBus.broadcast('CartQuantityChanged', 'OnAjaxComplete', {
            productId: productId,
            quantity: 0,
            quickAdd: true
        });
    }

    ensureUniqueQuickAddFormTarget(container)
    {
        const form = container.querySelector('form.quick-add-form');

        if (!form)
            return;

        const loadData = form.querySelector('load-data');

        if (!loadData)
            return;

        const guid = this.generateGuid();

        form.id = guid;

        loadData.dataset.target = `.quick-add-form[id='${guid}']`;
    }

    generateGuid()
    {
        if (window.crypto && typeof window.crypto.randomUUID === 'function')
            return window.crypto.randomUUID();

        return `${Date.now()}-${Math.floor(Math.random() * 1000000)}`;
    }
}

// Initialize
document.addEventListener('DOMContentLoaded', () =>
{
    window.quickAddHandler = new QuickAddHandler();
});

export default QuickAddHandler;